'use client';

import { useState, useCallback, useRef } from 'react';

import type { PupNameItem } from './nameGenerator.api';
import {
  defaultAnswers,
  type NameGeneratorAnswers
} from './nameGenerator.constants';

/**
 * Score a name based on user answers.
 * Weights based on legacy implementation.
 */
const WEIGHTS: Partial<Record<keyof NameGeneratorAnswers, number>> = {
  size: 4.0,
  color: 3.0,
  personality: 6.0,
  ambition: 2.0,
  belief: 3.0,
  season: 2.5,
  historicalFigure: 4.0,
  favouriteColor: 2.0,
  animal: 2.5,
  kind: 5.0
};

function calculateMatchScore(
  nameData: PupNameItem,
  answers: NameGeneratorAnswers
): number {
  let score = 0;

  (Object.keys(WEIGHTS) as Array<keyof typeof WEIGHTS>).forEach((key) => {
    const answerValue = answers[key as keyof NameGeneratorAnswers];
    const nameValue = nameData[key as keyof PupNameItem];
    const weight = WEIGHTS[key];

    if (weight === undefined || !nameValue) return;

    if (Array.isArray(answerValue)) {
      // Multi-select: check if any answer matches
      if (
        answerValue.some(
          (val) =>
            typeof val === 'string' &&
            typeof nameValue === 'string' &&
            nameValue.toLowerCase() === val.toLowerCase()
        )
      ) {
        score += weight;
      }
    } else if (answerValue && typeof nameValue === 'string') {
      // Single select
      if (nameValue.toLowerCase() === answerValue.toLowerCase()) {
        score += weight;
      }
    }
  });

  return score;
}

export function useNameGenerator(pupNames: PupNameItem[]) {
  const [breed, setBreed] = useState('');
  const [sex, setSex] = useState('female');
  const [answers, setAnswers] = useState<NameGeneratorAnswers>({
    ...defaultAnswers
  });
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showInputs, setShowInputs] = useState(true);

  const inputsRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const handleAnswerChange = useCallback(
    <K extends keyof NameGeneratorAnswers>(
      key: K,
      value: NameGeneratorAnswers[K]
    ) => {
      setAnswers((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleBreedChange = useCallback((newBreed: string) => {
    setBreed(newBreed);
  }, []);

  const handleSexChange = useCallback((newSex: string) => {
    setSex(newSex);
    setAnswers((prev) => ({ ...prev, sex: newSex }));
  }, []);

  const generateNames = useCallback(() => {
    setIsLoading(true);

    // Small delay for UX
    setTimeout(() => {
      // Filter by sex
      const filteredNames = pupNames.filter(
        (nameData) =>
          nameData.sex?.toLowerCase() === answers.sex.toLowerCase() ||
          nameData.sex?.toLowerCase() === sex.toLowerCase()
      );

      // Prioritize culture matches
      const cultureMatchedNames = filteredNames.filter(
        (nameData) =>
          nameData.culture?.toLowerCase() === answers.culture.toLowerCase()
      );

      // Score culture-matched names
      const scoredCultureNames = cultureMatchedNames.map((nameData) => ({
        ...nameData,
        score: calculateMatchScore(nameData, answers)
      }));

      scoredCultureNames.sort((a, b) => b.score - a.score);
      const topCultureNames = scoredCultureNames.slice(0, 5);

      // Get remaining names (not in culture matches)
      const remainingNames = filteredNames.filter(
        (nameData) =>
          !topCultureNames.some((topName) => topName.name === nameData.name)
      );

      // Score remaining names
      const scoredRemainingNames = remainingNames.map((nameData) => ({
        ...nameData,
        score: calculateMatchScore(nameData, answers)
      }));

      scoredRemainingNames.sort((a, b) => b.score - a.score);

      let topRemainingNames = scoredRemainingNames.slice(0, 2);

      // Fill up to 8 names if needed
      if (topCultureNames.length + topRemainingNames.length < 8) {
        const neededNames =
          8 - (topCultureNames.length + topRemainingNames.length);
        const randomRemainingNames = remainingNames
          .filter(
            (nameData) =>
              !topRemainingNames.some(
                (topName) => topName.name === nameData.name
              )
          )
          .sort(() => 0.5 - Math.random())
          .slice(0, neededNames);

        topRemainingNames = [
          ...topRemainingNames,
          ...randomRemainingNames.map((n) => ({ ...n, score: 0 }))
        ];
      }

      const topNames = [...topCultureNames, ...topRemainingNames];
      setNameSuggestions(topNames.map((nameData) => nameData.name));
      setIsLoading(false);

      // Scroll to results
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  }, [pupNames, answers, sex]);

  const clearResults = useCallback(() => {
    setNameSuggestions([]);
    setBreed('');
    setSex('female');
    setAnswers({ ...defaultAnswers });
    setShowInputs(false);
  }, []);

  const startGenerator = useCallback(() => {
    setShowInputs(true);
    setTimeout(() => {
      if (inputsRef.current) {
        inputsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, []);

  return {
    breed,
    sex,
    answers,
    nameSuggestions,
    isLoading,
    showInputs,
    handleBreedChange,
    handleSexChange,
    handleAnswerChange,
    generateNames,
    clearResults,
    startGenerator,
    inputsRef,
    resultsRef
  };
}
